import { useMemo, useCallback } from 'react';
import type {
  SchedulingData,
  SchedulingState,
  SchedulingStep,
  PostAssignments,
  KontentinoPage,
  ScheduleResult,
  SchedulingProgress,
} from '../types';

interface SchedulingFlowReturn {
  currentStep: SchedulingStep;
  selectedPages: KontentinoPage[];
  postAssignments: PostAssignments;
  scheduleResults: ScheduleResult[];
  isScheduling: boolean;
  schedulingProgress: SchedulingProgress;
  handleUpdatePageSelection: (pageIds: string[]) => void;
  handleProceedToAssignment: () => void;
  handleAssignPosts: (assignments: PostAssignments) => void;
  handleStartScheduling: () => Promise<void>;
  handleBack: () => void;
  handleClose: () => void;
}

export function useSchedulingFlow(
  data: SchedulingData | null,
  widgetState: SchedulingState | null,
  setWidgetState: (state: SchedulingState | ((prev: SchedulingState) => SchedulingState)) => void
): SchedulingFlowReturn {
  const currentStep = widgetState?.currentStep || 'page_selection';
  const isScheduling = currentStep === 'scheduling';

  // Get selected pages
  const selectedPages = useMemo(() => {
    if (!data?.availablePages || !widgetState) return [];
    return data.availablePages.filter((page) =>
      widgetState.selectedPageIds.includes(page.id)
    );
  }, [data?.availablePages, widgetState?.selectedPageIds]);

  // Calculate scheduling progress
  const schedulingProgress: SchedulingProgress = useMemo(() => {
    if (!widgetState) {
      return {
        total: 0,
        completed: 0,
        successful: 0,
        failed: 0,
        currentIndex: 0,
      };
    }
    const results = widgetState.scheduleResults || [];
    const total = Object.values(widgetState.postAssignments || {}).reduce(
      (sum, posts) => sum + posts.length,
      0
    );
    return {
      total,
      completed: results.length,
      successful: results.filter((r) => r.success).length,
      failed: results.filter((r) => !r.success).length,
      currentIndex: results.length,
    };
  }, [widgetState?.scheduleResults, widgetState?.postAssignments]);

  // Handle page selection (update selection without advancing step)
  const handleUpdatePageSelection = useCallback(
    (pageIds: string[]) => {
      setWidgetState((prev) => ({
        ...prev,
        selectedPageIds: pageIds,
      }));
    },
    [setWidgetState]
  );

  // Proceed to assignment step
  const handleProceedToAssignment = useCallback(() => {
    setWidgetState((prev) => ({
      ...prev,
      currentStep: 'bulk_assignment',
    }));
  }, [setWidgetState]);

  // Handle post assignments
  const handleAssignPosts = useCallback(
    (assignments: PostAssignments) => {
      setWidgetState((prev) => ({
        ...prev,
        postAssignments: assignments,
      }));
    },
    [setWidgetState]
  );

  // Handle start scheduling
  const handleStartScheduling = useCallback(async () => {
    if (!widgetState) return;

    setWidgetState((prev) => ({
      ...prev,
      currentStep: 'scheduling',
      scheduleResults: [],
    }));

    // TODO: Call MCP tool to schedule posts
    // For now, simulate scheduling with mock results
    const assignments = widgetState.postAssignments;
    const results: ScheduleResult[] = [];

    for (const [_pageId, postIds] of Object.entries(assignments)) {
      for (const postId of postIds) {
        // Simulate delay
        await new Promise((resolve) => setTimeout(resolve, 500));

        // Mock result (90% success rate)
        const success = Math.random() > 0.1;
        const result: ScheduleResult = {
          postId,
          success,
          kontentinoPostId: success ? `post-${Math.floor(Math.random() * 10000)}` : undefined,
          kontentinoPostUrl: success
            ? `https://app.kontentino.com/posts/${Math.floor(Math.random() * 10000)}`
            : undefined,
          error: success ? undefined : 'Network timeout',
        };

        results.push(result);

        // Update state with new result
        setWidgetState((prev) => ({
          ...prev,
          scheduleResults: [...prev.scheduleResults, result],
        }));
      }
    }

    // Move to results step
    setWidgetState((prev) => ({
      ...prev,
      currentStep: 'results',
    }));
  }, [widgetState?.postAssignments, setWidgetState]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    setWidgetState((prev) => {
      const currentIndex = ['page_selection', 'bulk_assignment', 'review'].indexOf(
        prev.currentStep
      );
      if (currentIndex > 0) {
        const steps: SchedulingStep[] = ['page_selection', 'bulk_assignment', 'review'];
        return {
          ...prev,
          currentStep: steps[currentIndex - 1],
        };
      }
      return prev;
    });
  }, [setWidgetState]);

  // Handle close
  const handleClose = useCallback(() => {
    setWidgetState({
      currentStep: 'page_selection',
      selectedPageIds: [],
      postAssignments: {},
      scheduleResults: [],
    });
  }, [setWidgetState]);

  return {
    currentStep,
    selectedPages,
    postAssignments: widgetState?.postAssignments || {},
    scheduleResults: widgetState?.scheduleResults || [],
    isScheduling,
    schedulingProgress,
    handleUpdatePageSelection,
    handleProceedToAssignment,
    handleAssignPosts,
    handleStartScheduling,
    handleBack,
    handleClose,
  };
}
